/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { createAsyncThunk } from "@reduxjs/toolkit";
import { AuthenticationTypeV3 } from "@tivocorp/data-domain-lib";
import type {
  AuthConfigurationV3,
  DomainToken,
  ProvisionDevicesResponseV2
} from "@tivocorp/data-domain-lib";

import {
  buildClaimJwt,
  buildPrimaryCredentialJwt,
  localizedErrorFallback
} from "../../../model/utility";
import { LocaleKeys } from "../../../model/locale";
import { Log } from "../../../model/logging";
import {
  SignInFlow,
  SignInFlowEvent,
  SignInLogger,
  SignInStep
} from "../../../model/logging/SignInLogger";
import { SignInInteractor } from "../../../model/interactor";
import { ErrorTypes } from "../../../model/error";
import { DeviceModel } from "../../../model/device";
import { TokenManager } from "../../../model/auth";
import { RetryMechanism } from "../../../model/retrymechanism";
import { DiagnosticSeverity } from "../../../model/types";
import type { UnityRootState } from "../../UnityAppStore";
import {
  beginSignInAttempt,
  beginSignInSession,
  cancelSignInAttempt,
  completeSignInAttempt,
  createCriticalRetryOptions,
  endSignInSession,
  extractSignInErrorCode,
  failSignInAttempt,
  isDeviceIdentityRemovedError,
  markStep,
  markStepReached,
  persistProvisionData,
  recoverRemovedDeviceIdentity,
  SIGN_IN_FALLBACK_ERROR_CODE,
  toSignInErrorPayload
} from "../common";
import type { SignInErrorPayload } from "../signIn.types";

const TAG: string = "signIn/credentialsSignInThunks";

/** Diagnostic text, not user copy — goes to SignInLogger, never to a screen. */
const FLOW_FAILED_MESSAGE: string = "Credential sign-in flow failed";
const ERROR_CODE_NO_CONFIG: string = "NO_TIVO_NATIVE_CONFIG";
/**
 * `idpJwtToken` now has its own family (A400-A412), so an empty JWT reports the
 * condition it actually is instead of the generic sign-in catch-all.
 *
 * This branch is belt-and-braces: `fetchIdpJwtToken` throws
 * `IdpJwtTokenServiceError` with this same code for an empty payload, so the
 * guard below only fires if the interactor returns "" without throwing. Reusing
 * the number keeps both paths reporting one code for one condition.
 */
const ERROR_CODE_EMPTY_JWT: string = ErrorTypes.IDP_JWT_TOKEN_EMPTY_TOKEN_A411;
const EMPTY_JWT_REASON: string = "EMPTY_IDP_JWT";
const CANCEL_REASON_SIGN_IN_AGAIN: string = "sign_in_again";
const CANCEL_REASON_SCREEN_LEFT: string = "screen_left";

/**
 * Rejection for an attempt the user ended from the connection-error overlay.
 * The empty message is what keeps the sign-in error alert down: the user asked
 * to go back to the form, and there is no failure to report.
 */
const CANCELLED_PAYLOAD: SignInErrorPayload = {
  code: "",
  message: "",
  serviceCode: ""
};

/**
 * Phase B — Sign In with Credentials (tivoNativeIdp).
 * Per DRD §4.2 / §4.3.1:
 *   Step 2: /claim  (claimScheme + claimJwt)
 *   Step 3: v1/idpJwtToken (U/P → JWT)
 *   Step 4: /bind   (bindScheme + primaryCredential + jwtToken)
 *   Step 5: /domainToken (domainTokenScheme + primaryCredential)
 *   Step 6: v2/provisionDevices
 */
export const signInWithCredentials = createAsyncThunk<
  { domainToken: DomainToken; provisionResponse: ProvisionDevicesResponseV2 },
  { userId: string; password: string; signal: AbortSignal },
  { state: UnityRootState; rejectValue: SignInErrorPayload }
>(
  "signIn/signInWithCredentials",
  async (
    params: { userId: string; password: string; signal: AbortSignal },
    { dispatch, getState, rejectWithValue }
  ) => {
    const flowStartTime: number = Date.now();
    let stepReached: string = SignInStep.Config;
    // Set once step 5 has handed its domain token to TokenManager.
    let hasAdoptedDomainToken: boolean = false;
    // This attempt's own signal: the screen's signal outlives the attempt, so
    // "Sign In Again" on the retry overlay ends the attempt through this one.
    const sessionSignal: AbortSignal = beginSignInSession(params.signal);
    try {
      Log.d(TAG, "signInWithCredentials() starting credential sign-in");
      SignInLogger.logFlowEvent(
        SignInFlowEvent.FlowStarted,
        "Credential sign-in flow started",
        DiagnosticSeverity.Info,
        { flow: SignInFlow.Credentials }
      );
      const signInInteractor: SignInInteractor = new SignInInteractor();
      const state: UnityRootState = getState();
      const configs: AuthConfigurationV3[] = state.signIn.authConfigurations;
      const tivoNativeConfig: AuthConfigurationV3 | undefined = configs.find(
        (config: AuthConfigurationV3): boolean =>
          config.authenticationType === AuthenticationTypeV3.TivoNativeIdp
      );

      if (tivoNativeConfig === undefined) {
        SignInLogger.logFlowEvent(
          SignInFlowEvent.FlowFailed,
          FLOW_FAILED_MESSAGE,
          DiagnosticSeverity.Error,
          {
            flow: SignInFlow.Credentials,
            step_reached: SignInStep.Config,
            error_code: ERROR_CODE_NO_CONFIG
          }
        );
        return rejectWithValue({
          code: SIGN_IN_FALLBACK_ERROR_CODE,
          message: "No tivoNativeIdp auth configuration found",
          // Raised here, not by a service, so there is no response envelope.
          serviceCode: ""
        });
      }

      // A device that has signed in before has an auth context persisted; its
      // absence is the marker for a first-ever sign-in on this device.
      const lastAuthContext = await DeviceModel.getLastAuthContext();
      beginSignInAttempt({
        flow: SignInFlow.Credentials,
        authType: AuthenticationTypeV3.TivoNativeIdp,
        isFirstSignIn:
          lastAuthContext === undefined || lastAuthContext === null,
        // The user typed these credentials and pressed Sign In.
        isUserInitiated: true,
        msoName: DeviceModel.getTiVoSettings().getMsoName(),
        attemptedTypes: state.signIn.attemptedAuthTypes
      });

      // Step 2: Claim — self-sign the device public key with the device key
      stepReached = SignInStep.Claim;
      markStepReached(SignInStep.Claim);
      Log.d(TAG, "signInWithCredentials() Step 2: claiming device");
      const claimScheme: string = tivoNativeConfig.authorizationSchemes.claim;
      const claimJwt: string = await buildClaimJwt();
      const claimStartedAt: number = Date.now();
      await RetryMechanism.callServiceWithRetry(
        () =>
          signInInteractor.fetchClaim({
            authorization: claimScheme + " " + claimJwt
          }),
        [],
        createCriticalRetryOptions(dispatch, {
          signInAgainSignal: sessionSignal
        }),
        sessionSignal
      );
      markStep(SignInStep.Claim, Date.now() - claimStartedAt);

      const bindScheme: string = tivoNativeConfig.authorizationSchemes.bind;
      const domainTokenScheme: string =
        tivoNativeConfig.authorizationSchemes.domainToken;
      const deviceId: string = params.userId + "_device";
      const dlid: string = DeviceModel.getDeviceLoggingId();

      // Step 3 — Get IDP JWT token (CRITICAL retry)
      // deviceId embeds the user-supplied userId, so it is never logged.
      stepReached = SignInStep.IdpJwtToken;
      markStepReached(SignInStep.IdpJwtToken);
      Log.d(TAG, "signInWithCredentials() Step 3: fetching IDP JWT token");
      const idpStartedAt: number = Date.now();
      const jwtToken: string = await RetryMechanism.callServiceWithRetry(
        () =>
          signInInteractor.fetchIdpJwtToken(
            params.userId,
            params.password,
            deviceId
          ),
        [],
        createCriticalRetryOptions(dispatch, {
          signInAgainSignal: sessionSignal
        }),
        sessionSignal
      );
      markStep(SignInStep.IdpJwtToken, Date.now() - idpStartedAt);

      if (typeof jwtToken !== "string" || jwtToken.length === 0) {
        Log.e(
          TAG,
          "signInWithCredentials() IDP JWT token missing/empty — " +
            "check TIdpJwtResponse field name matches the IDP response payload"
        );
        SignInLogger.logFlowEvent(
          SignInFlowEvent.FlowFailed,
          FLOW_FAILED_MESSAGE,
          DiagnosticSeverity.Error,
          {
            flow: SignInFlow.Credentials,
            step_reached: stepReached,
            error_code: ERROR_CODE_EMPTY_JWT,
            reason: EMPTY_JWT_REASON,
            duration_ms: String(Date.now() - flowStartTime)
          }
        );
        failSignInAttempt(ERROR_CODE_EMPTY_JWT);
        return rejectWithValue({
          code: ERROR_CODE_EMPTY_JWT,
          message: "IDP JWT token is empty",
          // The call succeeded; the payload was unusable. No error envelope.
          serviceCode: ""
        });
      }

      // Persist the JWT so we can silently mint a new domain token when the
      // current one expires (mid-session or on app boot).
      await DeviceModel.setIdpJwtToken(jwtToken);

      // Step 4 — Bind (primary credential + JWT token) (CRITICAL retry)
      stepReached = SignInStep.Bind;
      markStepReached(SignInStep.Bind);
      Log.d(TAG, "signInWithCredentials() Step 4: binding device");
      const primaryCredential: string = await buildPrimaryCredentialJwt();
      const bindStartedAt: number = Date.now();
      await RetryMechanism.callServiceWithRetry(
        () =>
          signInInteractor.fetchBind({
            authorization: bindScheme + " " + primaryCredential + " " + jwtToken
          }),
        [],
        createCriticalRetryOptions(dispatch),
        sessionSignal
      );
      markStep(SignInStep.Bind, Date.now() - bindStartedAt);

      Log.d(TAG, "signInWithCredentials() Step 4 completed: bind succeeded");

      // Step 5 — Get Domain Token (domainTokenScheme + primary credential) (CRITICAL retry)
      stepReached = SignInStep.DomainToken;
      markStepReached(SignInStep.DomainToken);
      const domainTokenStartedAt: number = Date.now();
      Log.d(
        TAG,
        "signInWithCredentials() Step 5: fetching domain token, " +
          "domainTokenScheme=" +
          domainTokenScheme
      );

      const domainToken: DomainToken =
        await RetryMechanism.callServiceWithRetry(
          () =>
            signInInteractor.fetchDomainToken({
              authorization: domainTokenScheme + " " + primaryCredential
            }),
          [],
          createCriticalRetryOptions(dispatch),
          sessionSignal
        );
      markStep(SignInStep.DomainToken, Date.now() - domainTokenStartedAt);

      // Never log the token itself — it is a live bearer credential.
      Log.d(
        TAG,
        "signInWithCredentials() Step 5 completed: domain token received, " +
          "expires_at=" +
          String(domainToken.expiration)
      );

      // Hand the token to its owner: TokenManager caches it and persists it
      // (no storage expiry; refresh is driven by the token's own expiration).
      await TokenManager.adoptToken(domainToken, null);
      hasAdoptedDomainToken = true;

      // Step 6 — Provision Devices (v2) (CRITICAL retry)
      stepReached = SignInStep.ProvisionDevices;
      markStepReached(SignInStep.ProvisionDevices);
      const provisionStartedAt: number = Date.now();
      Log.d(TAG, "signInWithCredentials() Step 6: provisioning devices (v2)");
      const provisionResponse: ProvisionDevicesResponseV2 =
        await RetryMechanism.callServiceWithRetry(
          () =>
            signInInteractor.createProvisionDevicesV2(
              {
                provisionDevicesRequestV2: {
                  authenticationType: tivoNativeConfig.authenticationType,
                  deviceLoggingId: dlid,
                  deviceConfig: {}
                }
              },
              domainToken.token
            ),
          [],
          createCriticalRetryOptions(dispatch),
          sessionSignal
        );
      markStep(SignInStep.ProvisionDevices, Date.now() - provisionStartedAt);

      // The provisioning payload carries bodyId / partnerId — do not log it.
      Log.d(
        TAG,
        "signInWithCredentials() Step 6 completed: device provisioned"
      );

      // Persist the auth context so a later token refresh uses the schemes the
      // auth-config service advertised for this flow instead of defaults.
      await DeviceModel.setLastAuthContext({
        authenticationType: AuthenticationTypeV3.TivoNativeIdp,
        authorizationSchemes: {
          claim: claimScheme,
          bind: bindScheme,
          domainToken: domainTokenScheme
        }
      });

      await persistProvisionData(provisionResponse);

      // Sign-in always lands in the account context; a profile is chosen later.
      await TokenManager.setActiveContext(null);

      SignInLogger.logFlowEvent(
        SignInFlowEvent.FlowCompleted,
        "Credential sign-in flow completed",
        DiagnosticSeverity.Info,
        {
          flow: SignInFlow.Credentials,
          auth_type: String(AuthenticationTypeV3.TivoNativeIdp),
          duration_ms: String(Date.now() - flowStartTime)
        }
      );
      // Closed after persistProvisionData(), so Sage identity exists and the
      // event ships attributed rather than through the pending queue.
      completeSignInAttempt();
      return { domainToken, provisionResponse };
    } catch (error: unknown) {
      // Ended by the user (Sign In Again on the retry overlay) or by the screen
      // going away — not a failure, so no failure telemetry and no error
      // alert: the form is already on screen with what they typed.
      if (sessionSignal.aborted) {
        Log.i(TAG, "signInWithCredentials() attempt cancelled");
        if (hasAdoptedDomainToken) {
          await TokenManager.clear();
        }
        cancelSignInAttempt(
          params.signal.aborted
            ? CANCEL_REASON_SCREEN_LEFT
            : CANCEL_REASON_SIGN_IN_AGAIN
        );
        return rejectWithValue(CANCELLED_PAYLOAD);
      }
      SignInLogger.logFlowFailure(
        SignInFlowEvent.FlowFailed,
        FLOW_FAILED_MESSAGE,
        error,
        {
          flow: SignInFlow.Credentials,
          step_reached: stepReached,
          duration_ms: String(Date.now() - flowStartTime)
        }
      );
      failSignInAttempt(extractSignInErrorCode(error));
      Log.e(TAG, "signInWithCredentials()", error);
      // Step 5 persisted a domain token, but without a provisioned device it
      // is not a session. Left in IndexedDB, the next cold boot restores it as
      // one and runs the post-sign-in chain (deviceFeatureSearch onwards)
      // against a device that never provisioned — e.g. after A463 + Exit.
      if (hasAdoptedDomainToken) {
        await TokenManager.clear();
      }
      // Resolved here rather than in a module constant: `Locale.setLocale`
      // fills the string map asynchronously after import, so a top-level
      // `const` would capture "" and never see a locale change.
      const payload: SignInErrorPayload = toSignInErrorPayload(
        error,
        localizedErrorFallback(LocaleKeys.SIGN_IN_CREDENTIALS_ERROR_MESSAGE)
      );
      // DPS 410: the device identity was removed server-side. Regenerate it
      // now, so the Retry on the alert — which restarts the walk from claim —
      // goes out with the new identity and can succeed. The service's own
      // text ("DLID is permanently removed.") means nothing to the user.
      if (isDeviceIdentityRemovedError(error)) {
        const recovered: boolean = await recoverRemovedDeviceIdentity(
          SignInFlow.Credentials
        );
        if (recovered) {
          return rejectWithValue({
            code: payload.code,
            message: localizedErrorFallback(
              LocaleKeys.SIGN_IN_DEVICE_IDENTITY_RESET_MESSAGE
            ),
            serviceCode: payload.serviceCode
          });
        }
      }
      return rejectWithValue(payload);
    } finally {
      endSignInSession(sessionSignal);
    }
  }
);
